// A Server-Sent Events reader over fetch, so the request can carry the bearer
// token (EventSource can't send headers).
import { ApiError, UNAUTHORIZED } from "./api";

export interface SSEEvent {
  event: string;
  data: string;
}

/** Splits a text stream into events. Feed it chunks as they arrive. */
export function sseParser(onEvent: (e: SSEEvent) => void) {
  let buf = "";
  let event = "";
  let data: string[] = [];
  const line = (l: string) => {
    if (l === "") {
      if (data.length) onEvent({ event: event || "message", data: data.join("\n") });
      event = "";
      data = [];
      return;
    }
    if (l.startsWith(":")) return;
    const i = l.indexOf(":");
    const field = i < 0 ? l : l.slice(0, i);
    const value = i < 0 ? "" : l.slice(i + 1).replace(/^ /, "");
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
  };
  return (chunk: string) => {
    buf += chunk;
    let i: number;
    while ((i = buf.search(/\r\n|\r|\n/)) >= 0) {
      const nl = buf.startsWith("\r\n", i) ? 2 : 1;
      line(buf.slice(0, i));
      buf = buf.slice(i + nl);
    }
  };
}

/**
 * Opens an event stream and calls onEvent for each event until the server
 * closes it or signal aborts. Rejects with an ApiError if the server refuses.
 */
export async function readEventStream(
  url: string,
  token: string,
  onEvent: (e: SSEEvent) => void,
  signal: AbortSignal,
  fetchImpl: typeof fetch = (...a) => fetch(...a),
): Promise<void> {
  let res: Response;
  try {
    res = await fetchImpl(url, { headers: { Accept: "text/event-stream", Authorization: `Bearer ${token}` }, signal, cache: "no-store" });
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    throw new ApiError(0, "network_error", "Lost the connection to the engine.");
  }
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event(UNAUTHORIZED));
    throw await ApiError.fromResponse(res);
  }
  if (!res.body) throw new ApiError(0, "network_error", "This browser can't read live updates.");
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  const push = sseParser(onEvent);
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return;
      push(value);
    }
  } finally {
    reader.releaseLock();
  }
}
